// Email Template Types

export type TriggerType = 'user_action' | 'admin_action' | 'scheduled' | 'system';
export type ContentFormat = 'html' | 'mjml';
export type VariableDataType = 'string' | 'number' | 'date' | 'boolean' | 'html' | 'url';

export interface EmailTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;

  // Template Content
  subject_template: string;
  html_content: string;
  text_content: string | null;
  content_format: ContentFormat;

  // Sender Configuration
  from_name: string;
  from_email: string;
  reply_to_email: string | null;

  // Recipient Configuration
  default_to: string | null;
  cc_addresses: string[] | null;
  bcc_addresses: string[] | null;

  // Trigger Information
  trigger_type: TriggerType;
  trigger_description: string;
  trigger_function: string;
  trigger_screen: string | null;
  cadence: string | null;

  // Status
  is_active: boolean;
  is_system: boolean;

  // Timestamps
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;

  // Relations (populated when fetched with joins)
  current_version?: EmailTemplateVersion;
  variables?: EmailTemplateVariable[];
  version_count?: number;
}

export interface EmailTemplateVersion {
  id: string;
  template_id: string;
  version_number: number;
  version_label: string;
  subject_template: string;
  html_content: string;
  text_content: string | null;
  change_summary: string | null;
  is_current: boolean;
  created_at: string;
  created_by: string | null;

  // Joined data
  creator?: {
    id: string;
    email: string;
  };
}

export interface EmailTemplateVariable {
  id: string;
  template_id: string;
  variable_name: string;
  variable_key: string;
  description: string | null;
  data_type: VariableDataType;
  is_required: boolean;
  default_value: string | null;
  example_value: string | null;
  source_table: string | null;
  source_field: string | null;
  sort_order: number;
  created_at: string;
}

export interface EmailTemplateAuditLog {
  id: string;
  template_id: string;
  version_id: string | null;
  admin_user_id: string | null;
  action: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  notes: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;

  // Joined data
  admin_user?: {
    id: string;
    email: string;
  };
}

// API Request/Response Types

export interface EmailTemplateListFilters {
  search?: string;
  status?: 'all' | 'active' | 'inactive';
  trigger_type?: TriggerType | 'all';
  page?: number;
  limit?: number;
}

export interface EmailTemplateListResponse {
  templates: EmailTemplate[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface UpdateEmailTemplateRequest {
  name?: string;
  description?: string | null;
  subject_template?: string;
  html_content?: string;
  text_content?: string | null;
  from_name?: string;
  from_email?: string;
  reply_to_email?: string | null;
  cc_addresses?: string[] | null;
  bcc_addresses?: string[] | null;
  is_active?: boolean;
  change_summary?: string;
}

export interface CreateEmailTemplateRequest {
  slug: string;
  name: string;
  description?: string | null;
  subject_template: string;
  html_content: string;
  text_content?: string | null;
  from_name?: string;
  from_email?: string;
  reply_to_email?: string | null;
  default_to?: string | null;
  cc_addresses?: string[] | null;
  bcc_addresses?: string[] | null;
  trigger_type: TriggerType;
  trigger_description: string;
  trigger_function: string;
  trigger_screen?: string | null;
  cadence?: string | null;
}

export interface SendTestEmailRequest {
  template_id: string;
  test_email: string;
  variables?: Record<string, string>;
}

export interface SendTestEmailResponse {
  success: boolean;
  message: string;
  resend_email_id?: string;
}

export interface RevertVersionRequest {
  template_id: string;
  version_id: string;
}

export interface UpdateVariablesRequest {
  template_id: string;
  variables: Omit<EmailTemplateVariable, 'id' | 'template_id' | 'created_at'>[];
}

// UI State Types

export interface TemplateEditorState {
  isDirty: boolean;
  isHtmlMode: boolean;
  previewDevice: 'desktop' | 'mobile';
  activeTab: 'content' | 'settings' | 'trigger' | 'variables' | 'history';
}

export interface VariableInsertionContext {
  cursorPosition: number | null;
  editorRef: React.RefObject<HTMLTextAreaElement | null> | null;
}

// Helper type for trigger type display
export const TRIGGER_TYPE_LABELS: Record<TriggerType, string> = {
  user_action: 'User Action',
  admin_action: 'Admin Action',
  scheduled: 'Scheduled',
  system: 'System',
};

// Helper type for cadence display
export const CADENCE_LABELS: Record<string, string> = {
  immediate: 'Immediate',
  on_demand: 'On Demand',
  weekly: 'Weekly',
  daily: 'Daily',
  monthly: 'Monthly',
};

// Helper type for variable data types
export const VARIABLE_TYPE_LABELS: Record<VariableDataType, string> = {
  string: 'Text',
  number: 'Number',
  date: 'Date',
  boolean: 'Yes/No',
  html: 'HTML',
  url: 'URL',
};
